import { useCallback, useEffect, useState } from "react";
import * as api from "../../api/client";
import type { AdminJob } from "../../api/client";
import type { Theme } from "../../theme/pixel";

// Shared bits for the Card- and Deck-power admin tabs.
//
// Both tabs share the same shape: a "RUN SWEEP" button that enqueues a
// CPU-exclusive admin job, a polling loop that picks up an in-flight
// sweep on mount and watches it through to completion, and a sortable
// table with right-aligned numeric columns. The data shapes diverge
// (cards vs decks, three numeric columns vs five) but the chrome is
// identical — that's what lives here.

const POLL_INTERVAL_MS = 2000;

/**
 * Track a sweep job (`card_power_sweep` or `deck_power_sweep`) across
 * the queued → running → succeeded / failed lifecycle.
 *
 * `matches` is the predicate used both for picking up an in-flight job
 * on mount and for filtering the queue listing during polling. For
 * `card_power_sweep`, the predicate also has to check
 * `args.include_npc` so the Player and NPC sub-tabs don't claim each
 * other's job; for `deck_power_sweep` the predicate is just the kind.
 *
 * Calling `enqueue(args)` posts a new job and starts polling. The
 * returned `job` is the most-recent snapshot, useful for showing
 * "QUEUED…" vs "RUNNING…" on the button label. `onSuccess` fires
 * after a successful run completes (typical use: reload the report).
 */
export function useSweepJob(opts: {
  matches: (job: AdminJob) => boolean;
  enqueue: (args: Record<string, unknown>) => Promise<{ job_id: string }>;
  onSuccess?: () => void | Promise<void>;
  onError?: (err: unknown) => void;
}) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<AdminJob | null>(null);

  const { matches, enqueue, onSuccess, onError } = opts;

  // Pick up any in-flight sweep on mount so leaving and returning to
  // the tab doesn't hide live progress.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { jobs } = await api.listAdminJobs();
        if (cancelled) return;
        const live = jobs.find(
          (j) =>
            matches(j) && (j.status === "running" || j.status === "queued"),
        );
        if (live) setJobId(live.id);
      } catch {
        // Best-effort.
      }
    })();
    return () => {
      cancelled = true;
    };
    // matches comes in fresh each render; depending on it would
    // re-trigger the pickup on every render. The set of jobs we care
    // about doesn't change for the life of the component, so empty
    // deps + a ref-style match closure is the right call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll the queue until the tracked job lands in a terminal state.
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { jobs } = await api.listAdminJobs();
        const j = jobs.find((j) => j.id === jobId);
        if (cancelled) return;
        setJob(j ?? null);
        if (j && (j.status === "succeeded" || j.status === "failed")) {
          setJobId(null);
          if (j.status === "succeeded" && onSuccess) await onSuccess();
          if (j.status === "failed" && onError) onError(j.error ?? "unknown error");
        }
      } catch (err) {
        if (!cancelled && onError) onError(err);
      }
    };
    const interval = window.setInterval(tick, POLL_INTERVAL_MS);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [jobId, onSuccess, onError]);

  const run = useCallback(
    async (args: Record<string, unknown> = {}) => {
      try {
        const { job_id } = await enqueue(args);
        setJobId(job_id);
      } catch (err) {
        if (onError) onError(err);
      }
    },
    [enqueue, onError],
  );

  return { running: jobId != null, job, run };
}

/** Sortable column header. Renders an arrow only when active. */
export function SortHeader({
  theme,
  active,
  align,
  onClick,
  children,
}: {
  theme: Theme;
  active: boolean;
  align?: "left" | "right";
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <th
      onClick={onClick}
      style={{
        ...thStyle(theme, align ?? "left"),
        cursor: "pointer",
        color: active ? theme.ink : theme.inkFaint,
      }}
    >
      {children}
    </th>
  );
}

export function thStyle(
  theme: Theme,
  align: "left" | "right" = "left",
): React.CSSProperties {
  return {
    textAlign: align,
    padding: "6px 8px",
    color: theme.inkFaint,
    fontSize: 11,
    fontWeight: 400,
    borderBottom: `1px solid ${theme.panelEdge}`,
    background: theme.bgDeep,
    userSelect: "none",
  };
}

export function tdStyle(theme: Theme): React.CSSProperties {
  return {
    padding: "4px 8px",
    color: theme.ink,
  };
}
